import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  attentionClass,
  attentionForAge,
  attentionLabel,
  cogginsDocumentStatus,
  COGGINS_DUE_DAYS,
  farrierAttention,
  formatReportDate,
  medicalAttentionScore,
  RABIES_DUE_DAYS,
  WORMER_DUE_DAYS
} from '../../utils/horseReportUtils.ts';
import { exportHorseReportPdf } from '../../utils/exportHorseReportPdf.ts';
import CogginsViewLink from '../shared/CogginsViewLink.tsx';
import { AppIcon, AppIconName } from '../../icons/appIcons.tsx';
import HorsePhotoPlaceholder from '../common/HorsePhotoPlaceholder.tsx';

interface ReportHorse {
  id: string;
  name: string;
  breed?: string;
  stallNumber?: string;
  designation: string;
  status: string;
  imageUrl?: string;
  farrierName?: string;
  lastSeenByFarrier?: string;
  lastCoggins?: string;
  lastWormer?: string;
  lastRabies?: string;
  cogginsDocumentUrl?: string;
  cogginsDocumentOriginalName?: string;
  specialNeeds?: string;
  medicalNotes?: string;
  notes?: string;
  temperament?: string;
  ridingLevel?: string;
  color?: string;
  ownerFamily?: { familyName: string };
  assignedRanchGirls?: Array<{ firstName: string; lastName: string }>;
}

type ReportTab = 'medical' | 'farrier' | 'informational';

function AttentionBadge({ level }: { level: keyof typeof attentionLabel }) {
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${attentionClass[level]}`}>
      {attentionLabel[level]}
    </span>
  );
}

function ExportPdfButton({
  disabled,
  busy,
  onClick,
  label = 'Export PDF',
  className = ''
}: {
  disabled?: boolean;
  busy?: boolean;
  onClick: () => void;
  label?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled || busy}
      onClick={onClick}
      className={`shrink-0 bg-ggaa-green text-white px-4 py-3 rounded-lg font-medium hover:bg-ggaa-green/90 disabled:opacity-50 min-h-[44px] inline-flex items-center justify-center gap-2 ${className}`}
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      {busy ? 'Exporting…' : label}
    </button>
  );
}

function HorseThumb({ horse }: { horse: ReportHorse }) {
  return (
    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-gray-100 shrink-0 border border-gray-200">
      {horse.imageUrl ? (
        <img src={horse.imageUrl} alt={horse.name} className="w-full h-full object-cover" />
      ) : (
        <HorsePhotoPlaceholder size="sm" className="rounded-none" />
      )}
    </div>
  );
}

export default function HorseReports() {
  const [horses, setHorses] = useState<ReportHorse[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<ReportTab>('medical');
  const [showNeedsAttentionOnly, setShowNeedsAttentionOnly] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch('/api/horses', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success) setHorses(data.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const sortedHorses = useMemo(() => {
    const list = [...horses];
    if (tab === 'medical') {
      list.sort((a, b) => medicalAttentionScore(b) - medicalAttentionScore(a) || a.name.localeCompare(b.name));
    } else if (tab === 'farrier') {
      list.sort((a, b) => {
        const score = (l: string | undefined) => {
          const att = farrierAttention(l);
          return att === 'missing' ? 3 : att === 'overdue' ? 2 : att === 'due_soon' ? 1 : 0;
        };
        return score(b.lastSeenByFarrier) - score(a.lastSeenByFarrier) || a.name.localeCompare(b.name);
      });
    } else {
      list.sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [horses, tab]);

  const filteredHorses = useMemo(() => {
    if (!showNeedsAttentionOnly) return sortedHorses;
    if (tab === 'medical') {
      return sortedHorses.filter(h => medicalAttentionScore(h) > 0);
    }
    if (tab === 'farrier') {
      return sortedHorses.filter(h => farrierAttention(h.lastSeenByFarrier) !== 'ok');
    }
    return sortedHorses;
  }, [sortedHorses, showNeedsAttentionOnly, tab]);

  const runExportPdf = useCallback(
    (list: ReportHorse[], needsAttentionOnly: boolean) => {
      setExportingPdf(true);
      try {
        exportHorseReportPdf(tab, list, { needsAttentionOnly });
      } finally {
        setExportingPdf(false);
      }
    },
    [tab]
  );

  const exportVisibleList = useCallback(() => {
    const list = filteredHorses.length > 0 ? filteredHorses : sortedHorses;
    const needsAttentionOnly =
      tab !== 'informational' && showNeedsAttentionOnly && filteredHorses.length > 0;
    runExportPdf(list, needsAttentionOnly);
  }, [filteredHorses, sortedHorses, showNeedsAttentionOnly, tab, runExportPdf]);

  const tabs: { id: ReportTab; label: string; icon: AppIconName; desc: string }[] = [
    { id: 'medical', label: 'Medical', icon: 'medical', desc: 'Vaccines, wormer, Coggins dates & documents' },
    { id: 'farrier', label: 'Farrier', icon: 'farrier', desc: 'Last farrier visit and horses due for work' },
    { id: 'informational', label: 'Informational', icon: 'info', desc: 'Photos, assignments, notes & status' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ggaa-green" />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-10">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Horse Reports</h1>
        <p className="text-sm sm:text-base text-gray-600 mt-1">
          At-a-glance medical, farrier, and herd information for {horses.length} active horses. PDF export is on this
          page (not Manage Horses).
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex-1 text-left px-4 py-3 rounded-xl border min-h-[44px] transition-colors ${
              tab === t.id
                ? 'border-ggaa-green bg-ggaa-green/10 ring-2 ring-ggaa-green/30'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <span className="font-semibold text-gray-900 inline-flex items-center gap-2">
              <AppIcon name={t.icon} className="w-5 h-5 text-ggaa-green shrink-0" />
              {t.label}
            </span>
            <span className="hidden sm:block text-xs text-gray-500 mt-0.5">{t.desc}</span>
          </button>
        ))}
      </div>

      {tab !== 'informational' && (
        <label className="flex items-center gap-2 mb-3 text-sm text-gray-700 min-h-[44px]">
          <input
            type="checkbox"
            checked={showNeedsAttentionOnly}
            onChange={(e) => setShowNeedsAttentionOnly(e.target.checked)}
            className="h-5 w-5 text-ggaa-green rounded"
          />
          Show only horses needing attention
        </label>
      )}

      <div className="sticky top-0 z-10 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-12 lg:px-12 py-3 mb-4 bg-gray-50/95 backdrop-blur border-y border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-700">
          <span className="font-medium text-gray-900">{tabs.find(t => t.id === tab)?.label} report</span>
          {' · '}
          {filteredHorses.length > 0
            ? `${filteredHorses.length} horse(s) shown`
            : sortedHorses.length > 0
              ? 'No horses match the filter'
              : 'No horses'}
        </p>
        <ExportPdfButton
          className="w-full sm:w-auto"
          disabled={horses.length === 0}
          busy={exportingPdf}
          onClick={exportVisibleList}
        />
      </div>

      {filteredHorses.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500 space-y-4">
          <p>
            {showNeedsAttentionOnly && tab !== 'informational'
              ? 'No horses need attention in this report right now.'
              : 'No horses to display.'}
          </p>
          {sortedHorses.length > 0 && showNeedsAttentionOnly && tab !== 'informational' && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <ExportPdfButton
                label={`Export all ${sortedHorses.length} horses (PDF)`}
                busy={exportingPdf}
                onClick={() => runExportPdf(sortedHorses, false)}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredHorses.map(horse => (
            <article
              key={horse.id}
              className="bg-white rounded-xl border border-ggaa-green/20 shadow-sm p-4 sm:p-5"
            >
              {tab === 'medical' && <MedicalReportCard horse={horse} />}
              {tab === 'farrier' && <FarrierReportCard horse={horse} />}
              {tab === 'informational' && <InfoReportCard horse={horse} />}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportHeader({ horse }: { horse: ReportHorse }) {
  return (
    <div className="flex gap-3 sm:gap-4 mb-4">
      <HorseThumb horse={horse} />
      <div className="min-w-0 flex-1">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 break-words">{horse.name}</h2>
        <p className="text-sm text-gray-600">
          {[horse.breed, horse.stallNumber ? `Stall ${horse.stallNumber}` : null, horse.designation]
            .filter(Boolean)
            .join(' · ')}
        </p>
        <p className="text-xs text-gray-500 mt-1">{horse.status}</p>
      </div>
    </div>
  );
}

function MedicalReportCard({ horse }: { horse: ReportHorse }) {
  const rabies = attentionForAge(horse.lastRabies, RABIES_DUE_DAYS);
  const wormer = attentionForAge(horse.lastWormer, WORMER_DUE_DAYS);
  const cogginsDate = attentionForAge(horse.lastCoggins, COGGINS_DUE_DAYS);
  const cogginsDoc = cogginsDocumentStatus(horse);

  return (
    <>
      <ReportHeader horse={horse} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="flex justify-between gap-2 p-2 rounded-lg bg-gray-50">
          <span>Last rabies</span>
          <span className="text-right">
            {formatReportDate(horse.lastRabies)} <AttentionBadge level={rabies} />
          </span>
        </div>
        <div className="flex justify-between gap-2 p-2 rounded-lg bg-gray-50">
          <span>Last wormer</span>
          <span className="text-right">
            {formatReportDate(horse.lastWormer)} <AttentionBadge level={wormer} />
          </span>
        </div>
        <div className="flex justify-between gap-2 p-2 rounded-lg bg-gray-50">
          <span>Last Coggins test</span>
          <span className="text-right">
            {formatReportDate(horse.lastCoggins)} <AttentionBadge level={cogginsDate} />
          </span>
        </div>
        <div className="flex justify-between gap-2 p-2 rounded-lg bg-gray-50">
          <span>Coggins on file</span>
          <span className="text-right">
            {horse.cogginsDocumentUrl ? (
              <CogginsViewLink horseId={horse.id} className="text-ggaa-green font-medium hover:underline">
                View
              </CogginsViewLink>
            ) : (
              'None'
            )}{' '}
            <AttentionBadge level={cogginsDoc} />
          </span>
        </div>
      </div>
      {(horse.specialNeeds || horse.medicalNotes) && (
        <div className="mt-4 pt-3 border-t border-gray-100 space-y-2 text-sm">
          {horse.specialNeeds && (
            <p><span className="font-medium text-gray-700">Special needs:</span> {horse.specialNeeds}</p>
          )}
          {horse.medicalNotes && (
            <p><span className="font-medium text-gray-700">Medical notes:</span> {horse.medicalNotes}</p>
          )}
        </div>
      )}
    </>
  );
}

function FarrierReportCard({ horse }: { horse: ReportHorse }) {
  const farrier = farrierAttention(horse.lastSeenByFarrier);
  return (
    <>
      <ReportHeader horse={horse} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div className="p-2 rounded-lg bg-gray-50">
          <p className="text-xs text-gray-500 uppercase">Farrier</p>
          <p className="font-medium">{horse.farrierName || '—'}</p>
        </div>
        <div className="p-2 rounded-lg bg-gray-50">
          <p className="text-xs text-gray-500 uppercase">Last seen by farrier</p>
          <p className="font-medium flex flex-wrap items-center gap-2">
            {formatReportDate(horse.lastSeenByFarrier)}
            <AttentionBadge level={farrier} />
          </p>
        </div>
      </div>
    </>
  );
}

function InfoReportCard({ horse }: { horse: ReportHorse }) {
  const assigned =
    horse.assignedRanchGirls?.map(g => `${g.firstName} ${g.lastName}`).join(', ') || 'Not assigned';
  const owner =
    horse.designation === 'personal horse' && horse.ownerFamily
      ? horse.ownerFamily.familyName
      : 'GGAA';

  return (
    <>
      <ReportHeader horse={horse} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        <p><span className="text-gray-500">Color:</span> {horse.color || '—'}</p>
        <p><span className="text-gray-500">Temperament:</span> {horse.temperament || '—'}</p>
        <p><span className="text-gray-500">Riding level:</span> {horse.ridingLevel || '—'}</p>
        <p><span className="text-gray-500">Owner:</span> {owner}</p>
        <p className="sm:col-span-2"><span className="text-gray-500">Assigned:</span> {assigned}</p>
      </div>
      {(horse.notes || horse.specialNeeds) && (
        <div className="mt-4 pt-3 border-t border-gray-100 space-y-2 text-sm">
          {horse.specialNeeds && <p><span className="font-medium">Special needs:</span> {horse.specialNeeds}</p>}
          {horse.notes && <p><span className="font-medium">Notes:</span> {horse.notes}</p>}
        </div>
      )}
    </>
  );
}
